import React, { useMemo, useState } from 'react'
import { format } from 'date-fns'
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  BuildingLibraryIcon,
  DocumentTextIcon,
  GiftIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline'
import { Payout, Transaction } from '../../types'
import Skeleton from '../Skeleton'
import CreatorAvatar from './CreatorAvatar'
import { ErrorPanel } from './SubscriptionsList'
import {
  TransactionKind,
  TransactionRow,
  TransactionStatus,
  displayName,
  formatCents,
  toTransactionRows,
} from './subscriptionsView'

interface TransactionHistoryProps {
  transactions: Transaction[]
  /** Creator payouts, listed alongside payments under the Payouts filter. */
  payouts?: Payout[]
  /** Whose history this is: payments they made read as money out. */
  currentUserId?: number
  /** Heading for the other party's column: "Creator" for a subscriber. */
  counterpartyLabel?: string
  isLoading?: boolean
  error?: string | null
  onRetry?: () => void
}

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'subscription', label: 'Subscriptions' },
  { key: 'tip', label: 'Tips' },
  { key: 'payout', label: 'Payouts' },
  { key: 'refund', label: 'Refunds' },
] as const
type Filter = (typeof FILTERS)[number]['key']

const KIND_ICON: Record<TransactionKind, { Icon: typeof ArrowPathIcon; className: string }> = {
  subscription: { Icon: ArrowPathIcon, className: 'bg-accent/10 text-accent' },
  tip: { Icon: GiftIcon, className: 'bg-accent/10 text-accent' },
  purchase: { Icon: ShoppingBagIcon, className: 'bg-accent/10 text-accent' },
  payout: { Icon: BuildingLibraryIcon, className: 'bg-green-50 text-green-600' },
  refund: { Icon: ArrowUturnLeftIcon, className: 'bg-amber-50 text-amber-600' },
}

const STATUS_CHIP: Record<TransactionStatus, { label: string; className: string }> = {
  paid: { label: 'Paid', className: 'bg-green-50 text-green-700 border-green-200' },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200' },
  refunded: { label: 'Refunded', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  pending: { label: 'Pending', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  disputed: { label: 'Disputed', className: 'bg-red-50 text-red-700 border-red-200' },
}

const EMPTY_FILTERED: Record<Exclude<Filter, 'all'>, string> = {
  subscription: 'No subscription payments yet.',
  tip: 'No tips yet.',
  payout: 'No payouts yet.',
  refund: 'No refunds.',
}

// Mobile: icon · who and what · amount over status. From md up, five columns.
const ROW_GRID =
  'grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,0.9fr)_minmax(0,0.8fr)_6.5rem] md:gap-x-4'

export const StatusChip: React.FC<{ status: TransactionStatus }> = ({ status }) => {
  const { label, className } = STATUS_CHIP[status]
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${className}`}>
      {label}
    </span>
  )
}

const Amount: React.FC<{ row: TransactionRow }> = ({ row }) => {
  const money = formatCents(row.amountCents, row.currency)
  if (row.direction === 'payout') return <span className="text-gray-900">{money}</span>
  return row.direction === 'in' ? (
    <span className="text-green-600">+{money}</span>
  ) : (
    <span className="text-gray-900">&minus;{money}</span>
  )
}

const Row: React.FC<{ row: TransactionRow }> = ({ row }) => {
  const { Icon, className } = KIND_ICON[row.kind]
  const date = format(new Date(row.date), 'MMM d, yyyy')
  return (
    <li className={`${ROW_GRID} px-4 py-3.5 transition-colors hover:bg-gray-50 sm:px-5`}>
      <div className="flex items-center gap-3">
        <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${className}`}>
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <span className="hidden text-sm font-medium text-gray-900 md:inline">{row.label}</span>
      </div>

      <div className="flex min-w-0 items-center gap-2.5">
        {row.counterparty && (
          <span className="hidden md:block">
            <CreatorAvatar user={row.counterparty} size="sm" />
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-gray-900">
            {row.counterparty ? displayName(row.counterparty) : row.kind === 'payout' ? 'To your bank' : '—'}
          </p>
          {/* Phones have no Type or Date column: say both here. */}
          <p className="truncate text-xs text-gray-500 md:hidden">
            {row.label} · {date}
          </p>
          {row.counterparty && (
            <p className="hidden truncate text-xs text-gray-500 md:block">@{row.counterparty.username}</p>
          )}
        </div>
      </div>

      <p className="hidden text-sm text-gray-600 md:block">{date}</p>

      <div className="flex flex-col items-end gap-1 md:contents">
        <p className="whitespace-nowrap text-sm font-semibold tabular-nums md:text-right">
          <Amount row={row} />
        </p>
        <div className="md:flex md:justify-end">
          <StatusChip status={row.status} />
        </div>
      </div>
    </li>
  )
}

const RowSkeleton: React.FC = () => (
  <li className={`${ROW_GRID} px-4 py-3.5 sm:px-5`} data-testid="transaction-skeleton">
    <Skeleton className="h-9 w-9 rounded-full" />
    <div className="space-y-1.5">
      <Skeleton className="h-3.5 w-2/3" />
      <Skeleton className="h-3 w-1/3" />
    </div>
    <Skeleton className="hidden h-3.5 w-20 md:block" />
    <Skeleton className="h-3.5 w-16 justify-self-end" />
    <Skeleton className="hidden h-5 w-14 justify-self-end rounded-full md:block" />
  </li>
)

const TransactionHistory: React.FC<TransactionHistoryProps> = ({
  transactions,
  payouts = [],
  currentUserId,
  counterpartyLabel = 'With',
  isLoading = false,
  error = null,
  onRetry,
}) => {
  const [filter, setFilter] = useState<Filter>('all')
  const rows = useMemo(
    () => toTransactionRows(transactions, payouts, currentUserId),
    [transactions, payouts, currentUserId]
  )
  const shown = filter === 'all' ? rows : rows.filter((row) => row.kind === filter)

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-4 sm:px-5 md:flex-row md:items-center md:justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Transaction history</h2>
        <div role="group" aria-label="Filter transactions" className="flex flex-wrap gap-1.5">
          {FILTERS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              aria-pressed={filter === key}
              className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                filter === key
                  ? 'bg-accent text-terminal-bg shadow-sm'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {!isLoading && !error && shown.length > 0 && (
        <div className={`${ROW_GRID} hidden border-b border-gray-200 bg-gray-50 px-5 py-2.5 md:grid`}>
          {['Type', counterpartyLabel, 'Date', 'Amount', 'Status'].map((heading, i) => (
            <span
              key={heading}
              className={`text-xs font-medium uppercase tracking-wide text-gray-500 ${i >= 3 ? 'text-right' : ''}`}
            >
              {heading}
            </span>
          ))}
        </div>
      )}

      {isLoading ? (
        <ul className="divide-y divide-gray-200" aria-busy="true" aria-label="Loading transactions">
          {[0, 1, 2, 3].map((i) => (
            <RowSkeleton key={i} />
          ))}
        </ul>
      ) : error ? (
        <div className="p-4 sm:p-5">
          <ErrorPanel message={error} onRetry={onRetry} />
        </div>
      ) : shown.length === 0 ? (
        <div className="px-6 py-12 text-center motion-safe:animate-fade-in">
          <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
            <DocumentTextIcon className="h-6 w-6 text-gray-400" aria-hidden="true" />
          </span>
          <p className="font-medium text-gray-900">
            {rows.length === 0 ? 'No payments yet' : EMPTY_FILTERED[filter as Exclude<Filter, 'all'>]}
          </p>
          {rows.length === 0 && <p className="mt-1 text-sm text-gray-500">Payments will show here as they happen.</p>}
        </div>
      ) : (
        <>
          <ul className="divide-y divide-gray-200 motion-safe:animate-fade-in">
            {shown.map((row) => (
              <Row key={row.key} row={row} />
            ))}
          </ul>
          <p className="border-t border-gray-200 bg-gray-50 px-5 py-3 text-xs text-gray-500">
            Showing {shown.length} of {rows.length}
          </p>
        </>
      )}
    </section>
  )
}

export default TransactionHistory
